import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState('checking');
  const [details, setDetails] = useState(null);
  const pollCount = useRef(0);
  const MAX_POLLS = 6;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderId = params.get('order_id');

    if (!orderId) {
      setState('failed');
      return;
    }

    let cancelled = false;
    const checkStatus = async () => {
      if (cancelled) return;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/payments/razorpay/order-status/${encodeURIComponent(orderId)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('status fetch failed');
        const data = await res.json();
        setDetails(data);
        if (data.payment_status === 'paid') {
          setState('success');
          if (refreshUser) await refreshUser();
          toast.success('Premium activated! Welcome aboard.');
          return;
        }
        if (data.payment_status === 'failed') {
          setState('failed');
          return;
        }
        pollCount.current += 1;
        if (pollCount.current >= MAX_POLLS) {
          setState('timeout');
          return;
        }
        setTimeout(checkStatus, 2000);
      } catch (err) {
        pollCount.current += 1;
        if (pollCount.current >= MAX_POLLS) setState('timeout');
        else setTimeout(checkStatus, 2000);
      }
    };

    checkStatus();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16 flex items-start justify-center" data-testid="payment-success-page">
      <Seo title="Payment Successful" description="Your premium membership is now active." path="/payment/success" noIndex />
      <div className="container-luxury max-w-2xl">
        <div className="bg-white rounded-sm shadow-md border border-border/50 p-10 md:p-14 text-center">
          {state === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 text-[#D4AF37] mx-auto mb-6 animate-spin" />
              <h1 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-[#0F172A] mb-3">
                Confirming your payment…
              </h1>
              <p className="text-muted-foreground">
                Please don't close this window. This usually takes a few seconds.
              </p>
            </>
          )}

          {state === 'success' && (
            <>
              <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-12 w-12 text-green-600" />
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-medium mb-4">
                <Crown className="h-3.5 w-3.5" /> Premium Activated
              </div>
              <h1 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-[#0F172A] mb-3">
                Payment Successful
              </h1>
              <p className="text-muted-foreground mb-8">
                Your <strong className="text-[#0F172A] capitalize">{details?.plan_type}</strong> plan is now active.
                Listings and connects have been credited to your account.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-8 py-6"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
                <Button
                  onClick={() => navigate('/add-property')}
                  variant="outline"
                  className="px-8 py-6"
                  data-testid="list-property-btn"
                >
                  List a Property
                </Button>
              </div>
            </>
          )}

          {state === 'failed' && (
            <>
              <div className="h-20 w-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
              <h1 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-[#0F172A] mb-3">
                Payment failed or cancelled
              </h1>
              <p className="text-muted-foreground mb-8">
                We couldn't confirm your payment. If money was debited, it will be refunded automatically by
                your bank within 5–7 business days.
              </p>
              <Button onClick={() => navigate('/premium')} className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-8 py-6">
                Try Again
              </Button>
            </>
          )}

          {state === 'timeout' && (
            <>
              <Loader2 className="h-16 w-16 text-amber-500 mx-auto mb-6" />
              <h1 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-[#0F172A] mb-3">
                Still processing…
              </h1>
              <p className="text-muted-foreground mb-8">
                Your payment is taking longer than usual. We'll notify you over email once it's confirmed.
                You can safely navigate away — your plan will activate as soon as the payment clears.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate('/dashboard')} className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-8 py-6">
                  Go to Dashboard
                </Button>
                <Link to="/premium">
                  <Button variant="outline" className="px-8 py-6 w-full">Back to Plans</Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
